import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import SimpleBookViewer from '../components/SimpleBookViewer';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  Users, 
  BookOpen, 
  DollarSign, 
  TrendingUp,
  Calendar,
  Settings,
  Eye,
  Download,
  Search,
  Filter,
  Crown,
  Clock,
  MoreVertical,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  Key,
  LogIn,
  ArrowLeft,
  X,
  Coins,
  Plus,
  Minus,
  History,
  FileText,
  Lightbulb,
  MessageSquare,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const AdminPage = () => {
  const { user, API_BASE_URL } = useAuth();
  const [activeTab, setActiveTab] = useState('analytics');
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Pagination state for books
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_books: 0,
    books_per_page: 25,
    has_next: false,
    has_prev: false
  });
  
  // Pagination state for users
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [userPagination, setUserPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_users: 0,
    users_per_page: 25,
    has_next: false,
    has_prev: false
  });
  const [selectedBook, setSelectedBook] = useState(null);
  const [settings, setSettings] = useState({
    api_keys: {},
    providers: {}
  });
  const [apiKeyInputs, setApiKeyInputs] = useState({
    openai: '',
    anthropic: '',
    fal: '',
    gemini: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEditModal, setUserEditModal] = useState(false);
  const [impersonating, setImpersonating] = useState(null);
  const [originalAdmin, setOriginalAdmin] = useState(null);
  
  // Credit management state
  const [userCredits, setUserCredits] = useState(0);
  const [creditAdjustment, setCreditAdjustment] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [loadingCredits, setLoadingCredits] = useState(false);
  
  // Transaction history state
  const [userTransactions, setUserTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Delete confirmation state
  const [deletingBook, setDeletingBook] = useState(null);
  
  // Support tickets state
  const [supportTickets, setSupportTickets] = useState([]);

  useEffect(() => {
    fetchAnalytics();
    fetchUsers();
    fetchBooks();
    fetchSettings();
    fetchSupportTickets();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Fetch user credits when edit modal opens
  useEffect(() => {
    if (userEditModal && selectedUser) {
      fetchUserCredits(selectedUser.id);
      fetchUserTransactions(selectedUser.id);
    } else {
      // Reset credit form when modal closes
      setCreditAdjustment('');
      setCreditReason('');
      setUserCredits(0);
      setUserTransactions([]);
    }
  }, [userEditModal, selectedUser]);

  // Add keyboard shortcuts for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (userEditModal) {
        if (event.key === 'Escape') {
          setUserEditModal(false);
          setSelectedUser(null);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [userEditModal]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/users`, {
        params: {
          page: page,
          limit: 25
        }
      });
      
      // Handle new paginated response format
      if (response.data.users && response.data.pagination) {
        setUsers(response.data.users);
        setUserPagination(response.data.pagination);
        setCurrentUserPage(page);
      } else {
        // Fallback for old format (backward compatibility)
        setUsers(response.data);
      }
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch users');
      setLoading(false);
    }
  };

  const fetchBooks = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/books`, {
        params: {
          page: page,
          limit: 25
        }
      });
      
      // Handle new paginated response format
      if (response.data.books && response.data.pagination) {
        setBooks(response.data.books);
        setPagination(response.data.pagination);
        setCurrentPage(page);
      } else {
        // Fallback for old format (backward compatibility)
        setBooks(response.data);
      }
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch books');
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchSupportTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupportTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch support tickets:', error);
    }
  };

  const markAsWorkingOn = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_BASE_URL}/api/admin/support/tickets/${ticketId}/working-on`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket marked as working on');
      fetchSupportTickets(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const deleteTicket = async (ticketId) => {
    if (window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_BASE_URL}/api/admin/support/tickets/${ticketId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ticket deleted successfully');
        fetchSupportTickets(); // Refresh the list
      } catch (error) {
        toast.error('Failed to delete ticket');
      }
    }
  };

  const updateApiKey = async (provider, key) => {
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        api_keys: {
          [`${provider}_api_key`]: key
        }
      };
      
      await axios.post(`${API_BASE_URL}/api/admin/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const displayName = provider.toUpperCase();
      
      toast.success(`${displayName} key updated successfully`);
      setApiKeyInputs(prev => ({ ...prev, [provider]: '' }));
      fetchSettings();
    } catch (error) {
      console.error('Failed to update API key:', error);
      toast.error('Failed to update API key');
    }
  };

  // User Management Functions
  const impersonateUser = async (targetUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/impersonate`, 
        { user_id: targetUser.id },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Store original admin info AND original token
      setOriginalAdmin(user);
      setImpersonating(targetUser);
      
      // Store the original admin token before overwriting
      localStorage.setItem('original_admin_token', token);
      localStorage.setItem('impersonating', JSON.stringify(targetUser));
      localStorage.setItem('original_admin', JSON.stringify(user));
      
      // Update auth context with impersonated user token
      localStorage.setItem('token', response.data.access_token);
      
      toast.success(`Now impersonating ${targetUser.full_name}`);
      
      // Redirect to dashboard as the user
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      toast.error('Failed to impersonate user');
    }
  };

  const stopImpersonating = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/admin/stop-impersonate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Restore original admin token
      const originalAdminData = JSON.parse(localStorage.getItem('original_admin'));
      localStorage.removeItem('impersonating');
      localStorage.removeItem('original_admin');
      
      setImpersonating(null);
      setOriginalAdmin(null);
      
      toast.success('Stopped impersonating user');
      
      // Redirect back to admin
      window.location.href = '/admin';
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      toast.error('Failed to stop impersonation');
    }
  };

  const updateUserStatus = async (userId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User updated successfully');
      fetchUsers();
      setUserEditModal(false);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    }
  };

  const resetUserPassword = async (userId, userName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Password reset for ${userName}. New password: ${response.data.new_password}`);
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  // Credit management functions
  const fetchUserCredits = async (userId) => {
    try {
      setLoadingCredits(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { user_id: userId }
      });
      setUserCredits(response.data.balance || 0);
    } catch (error) {
      console.error('Failed to fetch user credits:', error);
      setUserCredits(0);
    } finally {
      setLoadingCredits(false);
    }
  };

  const adjustUserCredits = async () => {
    if (!creditAdjustment || !creditReason.trim()) {
      toast.error('Please enter both credit adjustment amount and reason');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/credits/adjust`, {
        user_id: selectedUser.id,
        amount: parseInt(creditAdjustment),
        reason: creditReason.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Successfully adjusted credits by ${creditAdjustment}. New balance: ${response.data.new_balance}`);
      setUserCredits(response.data.new_balance);
      setCreditAdjustment('');
      setCreditReason('');
      
      // Refresh transaction history after adjustment
      fetchUserTransactions(selectedUser.id);
    } catch (error) {
      console.error('Failed to adjust credits:', error);
      toast.error('Failed to adjust credits');
    }
  };

  const fetchUserTransactions = async (userId) => {
    try {
      setLoadingTransactions(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/credits/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { user_id: userId, limit: 10 }
      });
      setUserTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch user transactions:', error);
      setUserTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleDeleteBook = async (bookId, bookTitle) => {
    try {
      setDeletingBook(bookId);
      const token = localStorage.getItem('token');
      
      const confirmed = window.confirm(
        `Are you sure you want to delete "${bookTitle}"?\n\nThis action cannot be undone and will also delete any associated covers and metadata.`
      );
      
      if (!confirmed) {
        setDeletingBook(null);
        return;
      }
      
      await axios.delete(`${API_BASE_URL}/api/admin/books/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Book "${bookTitle}" deleted successfully`);
      
      // Refresh books list
      fetchBooks();
    } catch (error) {
      console.error('Failed to delete book:', error);
      toast.error('Failed to delete book');
    } finally {
      setDeletingBook(null);
    }
  };

  const handleViewBook = async (bookId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/books/${bookId}`);
      setSelectedBook(response.data);
    } catch (error) {
      toast.error('Failed to fetch book details');
    }
  };

  const handleDownloadBook = async (book, format = 'pdf') => {
    try {
      if (book.status !== 'completed') {
        toast.error('Book is not completed yet');
        return;
      }

      if (format === 'pdf') {
        // Use the PDF generation API
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/books/${book.id}/download/pdf`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          console.error('PDF API response not ok:', response.status);
          throw new Error('Failed to generate PDF');
        }
        
        const data = await response.json();
        
        // Convert base64 to blob and download
        const byteCharacters = atob(data.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success('Book downloaded as PDF');
      } else {
        // Text download
        const chapters = Object.keys(book.content).sort((a, b) => parseInt(a) - parseInt(b));
        const content = `${book.title}\n\nGenre: ${book.genre}\nChapters: ${book.chapters}\nCreated: ${new Date(book.created_at).toLocaleDateString()}\n\n${chapters.map(chapterNum => 
          `Chapter ${chapterNum}\n\n${book.content[chapterNum]}\n\n`
        ).join('')}`;
        
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${book.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success('Book downloaded as text');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download book');
    }
  };
  const filteredBooks = books.filter(book => {
    const matchesSearch = book.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || book.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-400"></div>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
            <p className="text-gray-300">You don't have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-300">Manage users, monitor book generation, and view analytics</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="flex overflow-x-auto bg-gray-900/50 p-1 rounded-xl border border-purple-500/20 scrollbar-hide">
            {[
              { id: 'analytics', label: 'Analytics', icon: TrendingUp },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'books', label: 'Books', icon: BookOpen },
              { id: 'support', label: 'Support', icon: MessageSquare },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-3 sm:px-6 py-3 rounded-lg font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span className="text-sm sm:text-base">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                <div className="flex items-center">
                  <Users className="h-10 w-10 text-blue-400 mr-4" />
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.users.total}</p>
                    <p className="text-gray-300">Total Users</p>
                    <p className="text-sm text-green-400">
                      {analytics.users.premium} premium
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                <div className="flex items-center">
                  <BookOpen className="h-10 w-10 text-purple-400 mr-4" />
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.books.total}</p>
                    <p className="text-gray-300">Books Generated</p>
                    <p className="text-sm text-green-400">
                      {analytics.books.completed} completed
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                <div className="flex items-center">
                  <DollarSign className="h-10 w-10 text-green-400 mr-4" />
                  <div>
                    <p className="text-2xl font-bold text-white">${analytics.revenue.total}</p>
                    <p className="text-gray-300">Total Revenue</p>
                    <p className="text-sm text-green-400">
                      ${analytics.revenue.monthly_recurring}/mo MRR
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                <div className="flex items-center">
                  <Crown className="h-10 w-10 text-yellow-400 mr-4" />
                  <div>
                    <p className="text-2xl font-bold text-white">
                      {Math.round((analytics.users.premium / analytics.users.total) * 100)}%
                    </p>
                    <p className="text-gray-300">Conversion Rate</p>
                    <p className="text-sm text-blue-400">
                      Premium subscribers
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Placeholder */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                <h3 className="text-xl font-bold text-white mb-4">User Growth</h3>
                <div className="h-64 flex items-center justify-center bg-gray-800/50 rounded-lg">
                  <p className="text-gray-400">Chart placeholder - User growth over time</p>
                </div>
              </div>
              
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                <h3 className="text-xl font-bold text-white mb-4">Revenue Trends</h3>
                <div className="h-64 flex items-center justify-center bg-gray-800/50 rounded-lg">
                  <p className="text-gray-400">Chart placeholder - Revenue trends</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_BASE_URL}/api/admin/export-users`, {
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    });
                    
                    if (response.ok) {
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast.success('Users exported successfully');
                    } else {
                      toast.error('Failed to export users');
                    }
                  } catch (error) {
                    console.error('Error exporting users:', error);
                    toast.error('Failed to export users');
                  }
                }}
                className="px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors flex items-center space-x-2 whitespace-nowrap"
              >
                <Download className="h-5 w-5" />
                <span>Export Users</span>
              </button>
            </div>

            {/* Users Table */}
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-purple-500/20 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">User</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Books</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Joined</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 min-w-[120px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center space-y-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                            <p className="text-gray-400">Loading users...</p>
                          </div>
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <p className="text-gray-400">No users found</p>
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-800/30">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-white font-medium">{user.full_name}</p>
                            <p className="text-gray-400 text-sm">{user.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {user.is_premium ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300">
                              <Crown className="h-3 w-3 mr-1" />
                              Premium
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-300">
                              Free
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-white">
                          {user.books_generated}
                        </td>
                        <td className="px-6 py-4 text-gray-300">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="relative group">
                            <button className="text-gray-400 hover:text-gray-300 transition-colors">
                              <MoreVertical className="h-4 w-4" />
                            </button>
                            <div className="absolute right-0 top-full mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[9999] min-w-[180px]">
                              <div className="py-2">
                                <button
                                  onClick={() => {
                                    setSelectedUser(user);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-sm"
                                >
                                  <Eye className="h-3 w-3 inline mr-2" />
                                  View Details
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setUserEditModal(true);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-sm"
                                >
                                  <Edit className="h-3 w-3 inline mr-2" />
                                  Edit User
                                </button>
                                <button
                                  onClick={() => impersonateUser(user)}
                                  className="block w-full text-left px-4 py-2 text-blue-300 hover:bg-gray-700 hover:text-blue-200 transition-colors text-sm"
                                >
                                  <LogIn className="h-3 w-3 inline mr-2" />
                                  Login as User
                                </button>
                                <button
                                  onClick={() => resetUserPassword(user.id, user.full_name)}
                                  className="block w-full text-left px-4 py-2 text-yellow-300 hover:bg-gray-700 hover:text-yellow-200 transition-colors text-sm"
                                >
                                  <Key className="h-3 w-3 inline mr-2" />
                                  Reset Password
                                </button>
                                {!user.is_admin && (
                                  <button
                                    onClick={() => deleteUser(user.id, user.full_name)}
                                    className="block w-full text-left px-4 py-2 text-red-300 hover:bg-gray-700 hover:text-red-200 transition-colors text-sm"
                                  >
                                    <Trash2 className="h-3 w-3 inline mr-2" />
                                    Delete User
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls for Users */}
              {userPagination.total_pages > 1 && (
                <div className="px-6 py-4 bg-gray-800/30 border-t border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-400">
                      Showing <span className="font-medium text-white">{((currentUserPage - 1) * userPagination.users_per_page) + 1}</span> to{' '}
                      <span className="font-medium text-white">
                        {Math.min(currentUserPage * userPagination.users_per_page, userPagination.total_users)}
                      </span>{' '}
                      of <span className="font-medium text-white">{userPagination.total_users}</span> users
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => fetchUsers(currentUserPage - 1)}
                        disabled={!userPagination.has_prev || loading}
                        className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>Previous</span>
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: userPagination.total_pages }, (_, i) => i + 1)
                          .filter(page => {
                            return (
                              page === 1 ||
                              page === userPagination.total_pages ||
                              (page >= currentUserPage - 1 && page <= currentUserPage + 1)
                            );
                          })
                          .map((page, idx, arr) => {
                            const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                            
                            return (
                              <React.Fragment key={page}>
                                {showEllipsisBefore && (
                                  <span className="px-2 text-gray-400">...</span>
                                )}
                                <button
                                  onClick={() => fetchUsers(page)}
                                  disabled={loading}
                                  className={`px-3 py-2 rounded-lg transition-colors ${
                                    page === currentUserPage
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  } disabled:opacity-50`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}
                      </div>
                      
                      <button
                        onClick={() => fetchUsers(currentUserPage + 1)}
                        disabled={!userPagination.has_next || loading}
                        className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                      >
                        <span>Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Books Tab */}
        {activeTab === 'books' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search books..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="generating">Generating</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Books Table */}
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-purple-500/20 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Book</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Author</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Chapters</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Created</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 min-w-[120px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center space-y-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                            <p className="text-gray-400">Loading books...</p>
                          </div>
                        </td>
                      </tr>
                    ) : filteredBooks.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <p className="text-gray-400">No books found</p>
                        </td>
                      </tr>
                    ) : (
                      filteredBooks.map((book) => (
                      <tr key={book.id} className="hover:bg-gray-800/30">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-white font-medium">{book.title}</p>
                            <p className="text-gray-400 text-sm">{book.genre}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-300">
                          {users.find(u => u.id === book.user_id)?.full_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4">
                          {book.status === 'completed' && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300">
                              Completed
                            </span>
                          )}
                          {book.status === 'generating' && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300">
                              <Clock className="h-3 w-3 mr-1 animate-spin" />
                              Generating
                            </span>
                          )}
                          {book.status === 'failed' && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-300">
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-white">
                          {book.chapters}
                        </td>
                        <td className="px-6 py-4 text-gray-300">
                          {new Date(book.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            {/* Login as User button */}
                            <button 
                              onClick={() => {
                                const bookOwner = users.find(u => u.id === book.user_id);
                                if (bookOwner) {
                                  impersonateUser(bookOwner);
                                } else {
                                  toast.error('Book owner not found');
                                }
                              }}
                              className="text-blue-400 hover:text-blue-300 transition-colors p-1 rounded hover:bg-blue-500/10"
                              title="Login as book owner"
                            >
                              <LogIn className="h-4 w-4" />
                            </button>
                            {/* Delete button */}
                            <button 
                              onClick={() => handleDeleteBook(book.id, book.title)}
                              className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 p-1 rounded hover:bg-red-500/10"
                              title="Delete Book"
                              disabled={deletingBook === book.id}
                            >
                              {deletingBook === book.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {pagination.total_pages > 1 && (
                <div className="px-6 py-4 bg-gray-800/30 border-t border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-400">
                      Showing <span className="font-medium text-white">{((currentPage - 1) * pagination.books_per_page) + 1}</span> to{' '}
                      <span className="font-medium text-white">
                        {Math.min(currentPage * pagination.books_per_page, pagination.total_books)}
                      </span>{' '}
                      of <span className="font-medium text-white">{pagination.total_books}</span> books
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => fetchBooks(currentPage - 1)}
                        disabled={!pagination.has_prev || loading}
                        className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>Previous</span>
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first, last, current, and pages around current
                            return (
                              page === 1 ||
                              page === pagination.total_pages ||
                              (page >= currentPage - 1 && page <= currentPage + 1)
                            );
                          })
                          .map((page, idx, arr) => {
                            // Add ellipsis if there's a gap
                            const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                            
                            return (
                              <React.Fragment key={page}>
                                {showEllipsisBefore && (
                                  <span className="px-2 text-gray-400">...</span>
                                )}
                                <button
                                  onClick={() => fetchBooks(page)}
                                  disabled={loading}
                                  className={`px-3 py-2 rounded-lg transition-colors ${
                                    page === currentPage
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  } disabled:opacity-50`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}
                      </div>
                      
                      <button
                        onClick={() => fetchBooks(currentPage + 1)}
                        disabled={!pagination.has_next || loading}
                        className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                      >
                        <span>Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-8">
            {/* API Keys Section */}
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Key className="h-6 w-6 mr-2" />
                API Keys Configuration
              </h3>
              
              <div className="space-y-6">
                {/* OpenAI API Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    OpenAI API Key (GPT-4, DALL-E)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="password"
                      placeholder="sk-..."
                      value={apiKeyInputs.openai}
                      onChange={(e) => setApiKeyInputs(prev => ({ ...prev, openai: e.target.value }))}
                      className="flex-1 px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <button 
                      onClick={() => updateApiKey('openai', apiKeyInputs.openai)}
                      disabled={!apiKeyInputs.openai}
                      className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Update
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${settings.api_keys?.openai_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <p className="text-xs text-gray-400">
                      {settings.api_keys?.openai_configured ? 
                        `Configured: ${settings.api_keys?.openai_partial || 'sk-...'}` : 
                        'Not configured'
                      } • Get your key from: https://platform.openai.com/api-keys
                    </p>
                  </div>
                </div>

                {/* Anthropic API Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Anthropic API Key (Claude)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="password"
                      placeholder="sk-ant-..."
                      value={apiKeyInputs.anthropic}
                      onChange={(e) => setApiKeyInputs(prev => ({ ...prev, anthropic: e.target.value }))}
                      className="flex-1 px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <button 
                      onClick={() => updateApiKey('anthropic', apiKeyInputs.anthropic)}
                      disabled={!apiKeyInputs.anthropic}
                      className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Update
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${settings.api_keys?.anthropic_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <p className="text-xs text-gray-400">
                      {settings.api_keys?.anthropic_configured ? 'Configured' : 'Not configured'} • Get your key from: https://console.anthropic.com/
                    </p>
                  </div>
                </div>

                {/* FAL.ai API Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    FAL.ai API Key (Image Generation)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="password"
                      placeholder="fal-..."
                      value={apiKeyInputs.fal}
                      onChange={(e) => setApiKeyInputs(prev => ({ ...prev, fal: e.target.value }))}
                      className="flex-1 px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <button 
                      onClick={() => updateApiKey('fal', apiKeyInputs.fal)}
                      disabled={!apiKeyInputs.fal}
                      className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Update
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${settings.api_keys?.fal_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <p className="text-xs text-gray-400">
                      {settings.api_keys?.fal_configured ? 'Configured' : 'Not configured'} • Get your key from: https://fal.ai/dashboard/keys
                    </p>
                  </div>
                </div>

                {/* Gemini API Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Google Gemini API Key (Alternative Text & Images)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="password"
                      placeholder="AIza..."
                      value={apiKeyInputs.gemini}
                      onChange={(e) => setApiKeyInputs(prev => ({ ...prev, gemini: e.target.value }))}
                      className="flex-1 px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <button 
                      onClick={() => updateApiKey('gemini', apiKeyInputs.gemini)}
                      disabled={!apiKeyInputs.gemini}
                      className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Update
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${settings.api_keys?.gemini_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <p className="text-xs text-gray-400">
                      {settings.api_keys?.gemini_configured ? 'Configured' : 'Not configured'} • Get your key from: https://makersuite.google.com/app/apikey
                    </p>
                  </div>
                </div>

                {/* Provider Selection */}
                <div className="border-t border-gray-700 pt-6">
                  <h4 className="text-lg font-semibold text-white mb-4">🤖 AI Provider Preferences</h4>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Primary Text Generation Provider
                      </label>
                      <select className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                        <option value="openai">OpenAI GPT-4</option>
                        <option value="anthropic">Anthropic Claude</option>
                        <option value="gemini">Google Gemini</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Image Generation Provider
                      </label>
                      <select className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                        <option value="openai">OpenAI DALL-E</option>
                        <option value="fal">FAL.ai</option>
                        <option value="gemini">Google Gemini</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment System Status */}
                <div className="border-t border-gray-700 pt-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Payment System</h4>
                  
                  <div className="bg-blue-900/30 border border-blue-500/30 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse"></div>
                      <span className="text-white font-medium">
                        Migrating to Dodo Payments
                      </span>
                    </div>
                    <p className="text-sm text-gray-300">
                      Payment processing is being upgraded to Dodo Payments for improved global coverage and better billing features.
                      Stripe configuration has been removed from the admin panel - all payment settings are now managed via environment variables.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                    💾 Save All Settings
                  </button>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <div className="space-y-6">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Support Tickets</h2>
                <div className="bg-purple-600/20 px-3 py-1 rounded-lg">
                  <span className="text-purple-300 font-medium">{supportTickets.length} Total</span>
                </div>
              </div>

              {supportTickets.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No support tickets yet</p>
                  <p className="text-sm">New tickets will appear here when users submit them</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-gray-300 font-medium">Priority</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-medium">User</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-medium">Category</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-medium">Subject</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-medium">Status</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-medium">Date</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportTickets.map((ticket) => (
                        <tr key={ticket._id} className="border-b border-gray-800 hover:bg-gray-800/30">
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              ticket.priority === 'urgent' ? 'bg-red-600/20 text-red-400' :
                              ticket.priority === 'high' ? 'bg-orange-600/20 text-orange-400' :
                              ticket.priority === 'medium' ? 'bg-yellow-600/20 text-yellow-400' :
                              'bg-gray-600/20 text-gray-400'
                            }`}>
                              {ticket.priority.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-white font-medium">{ticket.name}</div>
                            <div className="text-gray-400 text-sm">{ticket.email}</div>
                          </td>
                          <td className="py-3 px-4 text-gray-300">{ticket.category}</td>
                          <td className="py-3 px-4 text-white">{ticket.subject}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              ticket.status === 'open' ? 'bg-blue-600/20 text-blue-400' :
                              ticket.status === 'working_on' ? 'bg-orange-600/20 text-orange-400' :
                              ticket.status === 'responded' ? 'bg-purple-600/20 text-purple-400' :
                              'bg-green-600/20 text-green-400'
                            }`}>
                              {ticket.status === 'working_on' ? 'WORKING ON' : ticket.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-300 text-sm">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex space-x-2">
                              {ticket.status === 'open' && (
                                <button
                                  onClick={() => markAsWorkingOn(ticket._id)}
                                  className="px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs hover:bg-blue-600/30 transition-colors"
                                >
                                  Working On
                                </button>
                              )}
                              <button
                                onClick={() => deleteTicket(ticket._id)}
                                className="px-2 py-1 bg-red-600/20 text-red-400 rounded text-xs hover:bg-red-600/30 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Book Viewer Modal */}
      {selectedBook && (
        <SimpleBookViewer
          book={selectedBook}
          onClose={() => setSelectedBook(null)}
        />
      )}

      {/* User Details Modal */}
      {selectedUser && !userEditModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">User Details</h2>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Basic Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-400">Full Name</label>
                      <p className="text-white font-medium">{selectedUser.full_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Email</label>
                      <p className="text-white font-medium">{selectedUser.email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">User ID</label>
                      <p className="text-gray-300 text-sm font-mono">{selectedUser.id}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Account Status</label>
                      <div className="flex items-center space-x-2 mt-1">
                        {selectedUser.is_premium ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300">
                            <Crown className="h-3 w-3 mr-1" />
                            Premium
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-300">
                            Free
                          </span>
                        )}
                        {selectedUser.is_admin && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300">
                            <Shield className="h-3 w-3 mr-1" />
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Account Activity</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-400">Books Generated</label>
                      <p className="text-white font-medium">{selectedUser.books_generated}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Member Since</label>
                      <p className="text-white font-medium">
                        {new Date(selectedUser.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Last Active</label>
                      <p className="text-gray-300">
                        {selectedUser.last_login ? 
                          new Date(selectedUser.last_login).toLocaleDateString() : 
                          'Recently active'
                        }
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Account Status</label>
                      <p className="text-gray-300">
                        {selectedUser.is_premium ? 'Premium User' : `${selectedUser.books_generated} books created`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => setUserEditModal(true)}
                  className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                  <span>Edit User</span>
                </button>
                <button
                  onClick={() => impersonateUser(selectedUser)}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Login as User</span>
                </button>
                <button
                  onClick={() => resetUserPassword(selectedUser.id, selectedUser.full_name)}
                  className="flex items-center space-x-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  <Key className="h-4 w-4" />
                  <span>Reset Password</span>
                </button>
                {!selectedUser.is_admin && (
                  <button
                    onClick={() => deleteUser(selectedUser.id, selectedUser.full_name)}
                    className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete User</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Edit Modal */}
      {userEditModal && selectedUser && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            // Close modal when clicking on backdrop
            if (e.target === e.currentTarget) {
              setUserEditModal(false);
              setSelectedUser(null);
            }
          }}
        >
          <div className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Edit User</h2>
                <button
                  onClick={() => {
                    setUserEditModal(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const updates = {
                  full_name: formData.get('full_name'),
                  email: formData.get('email'),
                  is_premium: formData.get('is_premium') === 'on',
                  is_admin: formData.get('is_admin') === 'on'
                };
                updateUserStatus(selectedUser.id, updates);
              }}
              className="p-6"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - User Information */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                    User Information
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      defaultValue={selectedUser.full_name}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      defaultValue={selectedUser.email}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Permissions */}
                  <div>
                    <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2 mb-4">
                      Permissions
                    </h3>
                    <div className="space-y-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_premium"
                          defaultChecked={selectedUser.is_premium}
                          className="mr-3 w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                        />
                        <span className="text-gray-300">Premium Access</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_admin"
                          defaultChecked={selectedUser.is_admin}
                          className="mr-3 w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                        />
                        <span className="text-gray-300">Admin Access</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Right Column - Credit Management */}
                <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <Coins className="h-5 w-5 text-yellow-400 mr-2" />
                  Credit Management
                </h3>
                
                {/* Current Balance Display */}
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-600">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Current Balance:</span>
                    <div className="flex items-center">
                      {loadingCredits ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                      ) : (
                        <>
                          <Coins className="h-4 w-4 text-yellow-400 mr-1" />
                          <span className="text-white font-semibold">{userCredits}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Credit Adjustment */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Credit Adjustment
                    </label>
                    <input
                      type="number"
                      value={creditAdjustment}
                      onChange={(e) => setCreditAdjustment(e.target.value)}
                      placeholder="e.g., 100 or -50"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Use positive numbers to add credits, negative to remove
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Reason <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={creditReason}
                      onChange={(e) => setCreditReason(e.target.value)}
                      placeholder="e.g., Account credit bonus"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Credit Adjustment Button */}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={adjustUserCredits}
                    disabled={!creditAdjustment || !creditReason.trim()}
                    className="flex-1 px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    <Coins className="h-4 w-4 mr-2" />
                    Adjust Credits
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setUserEditModal(false);
                      setSelectedUser(null);
                    }}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Close
                  </button>
                </div>
                
                <div className="text-center">
                  <p className="text-xs text-gray-400 flex items-center justify-center">
                    <Lightbulb className="h-3 w-3 mr-1" />
                    Tip: You can also click outside the modal or use the Cancel button below to close
                  </p>
                </div>

                {/* Transaction History Section */}
                <div className="mt-6 space-y-3">
                  <h4 className="text-md font-semibold text-white flex items-center">
                    <History className="h-4 w-4 text-blue-400 mr-2" />
                    Recent Credit History
                  </h4>
                  
                  {loadingTransactions ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                    </div>
                  ) : userTransactions.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {userTransactions.map((transaction) => (
                        <div key={transaction.id} className="bg-gray-800 rounded-lg p-3 border border-gray-600">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className={`text-sm font-medium ${
                                  transaction.amount > 0 ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  {transaction.amount > 0 ? '+' : ''}{transaction.amount} credits
                                </span>
                                <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                                  {transaction.transaction_type.replace('_', ' ')}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400 mt-1">
                                {transaction.description || 'No description provided'}
                              </p>
                              <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                                <span>Balance: {transaction.balance_before} → {transaction.balance_after}</span>
                                <span>•</span>
                                <span>{new Date(transaction.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-400">
                      <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No credit transactions yet</p>
                    </div>
                  )}
                </div>
              </div>
              </div>

              <div className="flex justify-between items-center space-x-4 pt-4 border-t border-gray-700 mt-8">
                <p className="text-xs text-gray-400">
                  Changes to user profile require "Save Changes" • Credit adjustments are applied immediately
                </p>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setUserEditModal(false);
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;